<script lang="ts" setup>
import { ref, Ref, computed, onBeforeMount, onBeforeUnmount, inject } from 'vue';
import { useEvents } from '@/plugins/events';
import properties from '@/components/forms/properties';
import { RootEntry } from '@/components/builder/form/entries/RootEntry';
import { instanceOfPersonalEntry, PersonalType, PersonalEntry } from '.';
import { FormBuilderContract } from '@/components/builder/form';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';

defineOptions({
    name: 'personal-blueprint',
    components: {
        ...properties
    }
});

const props = defineProps({
  "blueprint": null,
  "entry": null,
  "form": null,
  "parent": null,
  "index": null
});

const { $events } = useEvents();
const blueprint = computed(() => props.blueprint);
const entryData = ref(new PersonalEntry()) as Ref<PersonalEntry>;
const level = inject<number>('level', 0);

const loadControlData = () =>
{
    return props.form.document.initEntry(props.blueprint, entryData.value, instanceOfPersonalEntry, props.index);
};

const setControlData = () =>
{
    entryData.value = loadControlData();
};

entryData.value = loadControlData();

onBeforeMount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOn(setControlData);
        else
            useControlReload($events, props.blueprint.name, level).eventOn(setControlData);
    }
});

onBeforeUnmount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOff();
        else
            useControlReload($events, props.blueprint.name, level).eventOff();
    }
});

const title = computed({
    get()
    {
        return entryData.value.data.title;
    },
    set(value: string)
    {
        entryData.value.data.title = value;
    }
});

const givenName = computed({
    get()
    {
        return entryData.value.data.givenName;
    },
    set(value: string)
    {
        entryData.value.data.givenName = value;
    }
});

const middleName = computed({
    get()
    {
        return entryData.value.data.middleName;
    },
    set(value: string)
    {
        entryData.value.data.middleName = value;
    }
});

const surname = computed({
    get()
    {
        return entryData.value.data.surname;
    },
    set(value: string)
    {
        entryData.value.data.surname = value;
    }
});

const readonly = computed(() => props.form.expressions.readonly(props.blueprint));
const required = computed(() => props.form.expressions.required(props.blueprint));
</script>

<template>
    <form-component-wrapper class="personal-component" :form="form" :parent="parent" :blueprint="blueprint">
        <template #default>
            <div class="form-group">
                <form-label :form="form" :blueprint="blueprint" :required="required" :entry="entryData" />
                <div class="row g-0">
                    <div class="col-2 pe-2" v-if="blueprint.hasTitle">
                        <input type="text" class="form-control" v-model="title" :placeholder="$t('[[[Tytuł]]]')" :readonly="readonly">
                    </div>
                    <div class="col pe-2">
                        <input type="text" class="form-control" v-model="givenName" :placeholder="$t('[[[Imię]]]')" :readonly="readonly">
                    </div>
                    <div class="col pe-2" v-if="blueprint.hasMiddleName">
                        <input type="text" class="form-control" v-model="middleName" :placeholder="$t('[[[Drugie imię]]]')" :readonly="readonly">
                    </div>
                    <div class="col">
                        <input type="text" class="form-control" v-model="surname" :placeholder="$t('[[[Nazwisko]]]')" :readonly="readonly">
                    </div>
                </div>
                <form-error-message :entry="entryData" name="title" />
                <form-error-message :entry="entryData" name="givenName" />
                <form-error-message :entry="entryData" name="middleName" />
                <form-error-message :entry="entryData" name="surname" />
                <form-error-message :entry="entryData" name="custom" />
                <form-help :form="form" :blueprint="blueprint" />
            </div>
        </template>
        <template #properties>
            <field-name :form="form" :blueprint="blueprint" v-model="blueprint.name" />
            <ideo-form-localize v-slot="{ locale }">
                <field-text v-model="blueprint.label[locale]" :label="$t('[[[Etykieta]]]')" />
            </ideo-form-localize>
            <field-checkbox v-model="blueprint.showLabel" :label="$t('[[[Wyświetlaj etykietę]]]')" />
            <h6>{{ $t('[[[Pole zawiera]]]') }}</h6>
            <field-checkbox v-model="blueprint.hasTitle" :label="$t('[[[Tytuł]]]')" />
            <field-checkbox v-model="blueprint.hasMiddleName" :label="$t('[[[Drugie imię]]]')" />
            <ideo-form-localize v-slot="{ locale }">
                <field-textarea v-model="blueprint.help[locale]" :label="$t('[[[Pomoc]]]')" />
            </ideo-form-localize>
            <field-visible :form="form" :blueprint="blueprint" />
            <field-readonly :form="form" :blueprint="blueprint" />
            <field-required :form="form" :blueprint="blueprint" />
            <field-error :form="form" :blueprint="blueprint" />
        </template>
    </form-component-wrapper>
</template>

<style lang="scss" scoped>
</style>
